/**
 * @file RoleGuard.jsx
 * Componente di protezione per rotte riservate a specifici ruoli.
 * Se il ruolo corrente non è nell'array `allow`, reindirizza alla pagina Pianificazioni.
 * Rimonta i figli al cambio utente, così le preferenze salvate non passano da un utente all'altro.
 * @example <RoleGuard allow={[ROLES.ADMIN, ROLES.PLANNER]}><AdminPage /></RoleGuard>
 */

import { Navigate } from 'react-router-dom';
import { Fragment } from 'react';
import { getPersistenceIdentity, identitySignature } from '../../lib/utils/persistenceIdentity';
import { useApp } from '../../hooks/useApp';
import { usePermissions } from '../../hooks/usePermissions';
import { ROUTES } from '../../lib/constants/routes';
import { ROLES } from '../../lib/constants/roles';

export default function RoleGuard({ allow, permission, fallback, children }) {
  const app = useApp();
  const { userRole } = app;
  const { can } = usePermissions();
  const role = userRole ?? ROLES.PREDEFINITO;
  const hasAccess = permission ? can(permission) : allow?.includes(role);
  if (!hasAccess) {
    const defaultFallback =
      role === ROLES.SALES_BACKOFFICE
        ? ROUTES.SETUP('clienti')
        : role === ROLES.USER
          ? ROUTES.PIANIFICAZIONE_DEFINITIVA
          : ROUTES.PLANNING;
    return <Navigate to={fallback ?? defaultFallback} replace />;
  }
  // La key dipende solo dall'email dell'utente autenticato: cambiando utente React
  // rimonta la pagina e gli hook di persistenza rileggono le preferenze del nuovo utente.
  // Cambiando ruolo la key resta uguale, quindi le preferenze restano le stesse.
  // "auth-pending" finché l'autenticazione non è pronta (nessuna lettura/scrittura).
  return <Fragment key={identitySignature(getPersistenceIdentity(app)) ?? "auth-pending"}>{children}</Fragment>;
}
